import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

interface MedecinRef {
  nom: string;
  prenom: string;
  specialite: string | null;
}

interface SageFemmeRef {
  nom: string;
  prenom: string;
}

interface Consultation {
  id: string;
  date_consultation: string;
  heure_debut: string | null;
  heure_fin: string | null;
  symptomes_signes: string | null;
  examen: string | null;
  diagnostic: string | null;
  suivi_vers: string | null;
  mot_fin: string | null;
  statut: string;
  medecins: MedecinRef | null;
  sage_femmes: SageFemmeRef | null;
  donnees_supplementaires: any | null;
}

interface Suivi {
  id: string;
  objet: string | null;
  message: string | null;
  diagnostique: string | null;
  traitement: string | null;
  notes_medicales: string | null;
  statut: string | null;
  date_creation: string | null;
  medecin_expediteur: MedecinRef | null;
  medecin_destinataire: MedecinRef | null;
}

interface Prescription {
  id: string;
  medicaments: any;
  diagnostic: string | null;
  instructions: string | null;
  duree_traitement: string | null;
  date_prescription: string | null;
  type_ordonnance: string | null;
  medecins: MedecinRef | null;
}

interface DossierAnalyse {
  id: string;
  date_analyse: string;
  statut: string;
  conclusion: string | null;
}

interface Soin {
  id: string;
  date: string | null;
  description: string;
  source: 'consultation' | 'suite';
  statut: string | null;
  medecin: MedecinRef | null;
  consultationId: string | null;
}

interface SuiteInfo {
  id: string;
  consultation_id: string | null;
  statut: string;
}

type SousOnglet = 'consultations' | 'soins' | 'suivis' | 'prescriptions' | 'analyses';

const SOUS_ONGLETS: { key: SousOnglet; label: string; icon: string }[] = [
  { key: 'consultations', label: 'Consultations', icon: 'ri-stethoscope-line' },
  { key: 'soins', label: 'Soins', icon: 'ri-nurse-line' },
  { key: 'suivis', label: 'Suivis', icon: 'ri-send-plane-line' },
  { key: 'prescriptions', label: 'Prescriptions', icon: 'ri-file-text-line' },
  { key: 'analyses', label: 'Analyses', icon: 'ri-microscope-line' },
];

const formatDate = (d: string | null) => {
  if (!d) return '—';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return d;
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatHeure = (t: string | null) => (t ? t.substring(0, 5) : '');

const statutBadge = (s: string | null | undefined) => {
  switch (s) {
    case 'termine':
      return { text: 'Terminé', cls: 'bg-green-100 text-green-700 border-green-200' };
    case 'en_cours':
      return { text: 'En cours', cls: 'bg-orange-100 text-orange-700 border-orange-200' };
    case 'brouillon':
      return { text: 'Brouillon', cls: 'bg-amber-100 text-amber-700 border-amber-200' };
    case 'en_attente':
      return { text: 'En attente', cls: 'bg-yellow-100 text-yellow-700 border-yellow-200' };
    case 'annule':
      return { text: 'Annulé', cls: 'bg-red-100 text-red-700 border-red-200' };
    default:
      return { text: s || '—', cls: 'bg-gray-100 text-gray-600 border-gray-200' };
  }
};

export default function HistoriqueSection({ patientId, patientUserRole }: { patientId: string; patientUserRole?: string }) {
  const navigate = useNavigate();
  const [sousOnglet, setSousOnglet] = useState<SousOnglet>('consultations');
  const [loading, setLoading] = useState(true);
  const [consultations, setConsultations] = useState<Consultation[]>([]);
  const [suivis, setSuivis] = useState<Suivi[]>([]);
  const [prescriptions, setPrescriptions] = useState<Prescription[]>([]);
  const [analyses, setAnalyses] = useState<DossierAnalyse[]>([]);
  const [soins, setSoins] = useState<Soin[]>([]);
  const [soinsByConsultation, setSoinsByConsultation] = useState<Record<string, Soin[]>>();
  const [expanded, setExpanded] = useState<string | null>(null);
  const [suiteMap, setSuiteMap] = useState<Record<string, SuiteInfo[]>>();

  useEffect(() => {
    if (!patientId) return;
    loadAll();
  }, [patientId]);

  const loadAll = async () => {
    setLoading(true);
    try {
      await Promise.all([loadConsultations(), loadSoins(), loadSuivis(), loadPrescriptions(), loadAnalyses(), loadSuites()]);
    } catch (e) {
      console.error('Erreur chargement historique:', e);
    } finally {
      setLoading(false);
    }
  };

  const loadConsultations = async () => {
    const { data, error } = await supabase
      .from('consultations')
      .select('*, medecins(nom, prenom, specialite)')
      .eq('patient_id', patientId)
      .order('date_consultation', { ascending: false })
      .order('heure_debut', { ascending: false })
      .limit(100);

    if (error || !data) return;

    const sageFemmeIds = data
      .filter((c: any) => !c.medecins && c.medecin_id)
      .map((c: any) => c.medecin_id);

    let sageFemmesMap: Record<string, SageFemmeRef> = {};
    if (sageFemmeIds.length > 0) {
      const { data: sfData } = await supabase
        .from('sage_femmes')
        .select('id, nom, prenom')
        .in('id', sageFemmeIds);
      if (sfData) {
        sfData.forEach((sf: any) => {
          sageFemmesMap[sf.id] = { nom: sf.nom, prenom: sf.prenom };
        });
      }
    }

    const enriched: Consultation[] = data.map((c: any) => ({
      ...c,
      sage_femmes: !c.medecins && c.medecin_id && sageFemmesMap[c.medecin_id]
        ? sageFemmesMap[c.medecin_id]
        : null,
    }));

    setConsultations(enriched);
  };

  const loadSuites = async () => {
    const { data } = await supabase
      .from('suites_soins')
      .select('id, consultation_id, statut')
      .eq('patient_id', patientId);
    if (!data) return;
    const map: Record<string, SuiteInfo[]> = {};
    data.forEach((s: any) => {
      if (!map[s.consultation_id]) map[s.consultation_id] = [];
      map[s.consultation_id].push({ id: s.id, consultation_id: s.consultation_id, statut: s.statut });
    });
    setSuiteMap(map);
  };

  const loadSuivis = async () => {
    const { data } = await supabase
      .from('suivis_patients')
      .select(`
        *,
        medecin_expediteur:medecins!medecin_expediteur_id(nom, prenom, specialite),
        medecin_destinataire:medecins!medecin_destinataire_id(nom, prenom, specialite)
      `)
      .eq('patient_id', patientId)
      .order('date_creation', { ascending: false });

    if (data) setSuivis(data as Suivi[]);
  };

  const loadPrescriptions = async () => {
    const { data } = await supabase
      .from('prescriptions')
      .select('*, medecins(nom, prenom, specialite)')
      .eq('patient_id', patientId)
      .order('date_prescription', { ascending: false });

    if (data) setPrescriptions(data as Prescription[]);
  };

  const loadAnalyses = async () => {
    const { data } = await supabase
      .from('dossiers_analyses')
      .select('id, date_analyse, statut, conclusion')
      .eq('patient_id', patientId)
      .order('date_analyse', { ascending: false });

    if (data) setAnalyses(data as DossierAnalyse[]);
  };

  const loadSoins = async () => {
    const { data: fiches } = await supabase
      .from('fiches_soins')
      .select('id, consultation_id, date_soin, soins_a_administrer, statut, medecin_id, medecins(nom, prenom, specialite)')
      .eq('patient_id', patientId)
      .order('date_soin', { ascending: false });

    let sessions: any[] = [];
    const suiteConsultationMap: Record<string, string | null> = {};
    const { data: suites } = await supabase
      .from('suites_soins')
      .select('id, consultation_id')
      .eq('patient_id', patientId);

    if (suites && suites.length > 0) {
      const suiteIds = suites.map((s: any) => s.id);
      suites.forEach((s: any) => {
        suiteConsultationMap[s.id] = s.consultation_id || null;
      });
      const { data: sessionsData } = await supabase
        .from('sessions_soins')
        .select('id, date_soin, description, medecin_id, suite_soin_id, medecins(nom, prenom, specialite)')
        .in('suite_soin_id', suiteIds)
        .order('date_soin', { ascending: false });
      sessions = sessionsData || [];
    }

    const merged: Soin[] = [
      ...(fiches || []).map((f: any) => ({
        id: f.id,
        date: f.date_soin,
        description: f.soins_a_administrer || '',
        source: 'consultation' as const,
        statut: f.statut || null,
        medecin: f.medecins || null,
        consultationId: f.consultation_id || null,
      })),
      ...sessions.map((s: any) => ({
        id: s.id,
        date: s.date_soin,
        description: s.description || '',
        source: 'suite' as const,
        statut: null,
        medecin: s.medecins || null,
        consultationId: suiteConsultationMap[s.suite_soin_id] ?? null,
      })),
    ].sort((a, b) => (b.date || '').localeCompare(a.date || ''));

    setSoins(merged);

    const map: Record<string, Soin[]> = {};
    merged.forEach((s) => {
      if (s.consultationId) {
        if (!map[s.consultationId]) map[s.consultationId] = [];
        map[s.consultationId].push(s);
      }
    });
    setSoinsByConsultation(map);
  };

  const renderMedicaments = (medicaments: any) => {
    if (!Array.isArray(medicaments) || medicaments.length === 0) return null;
    return (
      <div className="space-y-1.5">
        {medicaments.map((m: any, i: number) => {
          const nom = m?.nom || m?.medicament || m?.label || `Médicament ${i + 1}`;
          const posologie = m?.posologie || m?.dosage || '';
          return (
            <div key={i} className="flex items-center gap-2 text-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0"></span>
              <span className="font-medium text-green-900">{nom}</span>
              {posologie && <span className="text-green-700 text-xs">· {posologie}</span>}
            </div>
          );
        })}
      </div>
    );
  };

  const countFor = (key: SousOnglet) => {
    if (key === 'consultations') return consultations.length;
    if (key === 'soins') return soins.length;
    if (key === 'suivis') return suivis.length;
    if (key === 'prescriptions') return prescriptions.length;
    return analyses.length;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
        <i className="ri-loader-4-line text-3xl text-clinic-blue animate-spin"></i>
        <p className="mt-3 text-gray-500 text-sm">Chargement de l'historique...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Sous-onglets */}
      <div className="flex flex-wrap gap-1 px-4 py-3 border-b border-gray-100 bg-gray-50/60">
        {SOUS_ONGLETS.map((onglet) => {
          const active = sousOnglet === onglet.key;
          const count = countFor(onglet.key);
          return (
            <button
              key={onglet.key}
              onClick={() => setSousOnglet(onglet.key)}
              className={`px-3.5 py-2 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors flex items-center gap-1.5 ${
                active ? 'bg-clinic-blue text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
                            {onglet.label}
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${active ? 'bg-clinic-blue text-white' : 'bg-gray-200 text-gray-500'}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      <div className="p-5">
        {/* ===== CONSULTATIONS ===== */}
        {sousOnglet === 'consultations' && (
          consultations.length === 0 ? (
            <EmptyState icon="ri-stethoscope-line" text="Aucune consultation enregistrée" />
          ) : (
            <div className="space-y-3">
              {consultations.map((c) => {
                const st = statutBadge(c.statut);
                const isExpanded = expanded === c.id;
                const auteur = c.medecins
                  ? `Dr. ${c.medecins.nom} ${c.medecins.prenom}${c.medecins.specialite ? ` · ${c.medecins.specialite}` : ''}`
                  : c.sage_femmes
                    ? `Sage-femme ${c.sage_femmes.nom} ${c.sage_femmes.prenom}`
                    : 'Praticien';

                const suitesAssociees = suiteMap[c.id] || [];
                const hasSuites = suitesAssociees.length > 0;
                const suiteEnCours = suitesAssociees.some((s) => s.statut === 'en_cours');
                const suiteEnCoursItem = suitesAssociees.find((s) => s.statut === 'en_cours');
                const canFicheVisite = patientUserRole === 'medecin' || patientUserRole === 'super_admin';

                return (
                  <div key={c.id} className="border border-gray-200 rounded-xl overflow-hidden">
                    <button
                      onClick={() => setExpanded(isExpanded ? null : c.id)}
                      className="w-full flex items-center justify-between gap-3 p-4 hover:bg-gray-50 transition-colors cursor-pointer text-left"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900">
                            {formatDate(c.date_consultation)}
                            {formatHeure(c.heure_debut) && <span className="text-gray-400"> · {formatHeure(c.heure_debut)}</span>}
                          </p>
                          <p className="text-xs text-gray-500 truncate">{auteur}</p>
                          {c.diagnostic && (
                            <p className="text-xs text-red-600 mt-0.5 truncate">{c.diagnostic}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {hasSuites && (
                          suiteEnCours && suiteEnCoursItem ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/medecin/suite-soins?suiteId=${suiteEnCoursItem.id}`);
                              }}
                              className="text-[10px] px-2 py-0.5 rounded-full font-semibold border bg-orange-100 text-orange-700 border-orange-200 hover:bg-orange-200 cursor-pointer transition-colors whitespace-nowrap"
                            >
                              Suite en cours →
                            </button>
                          ) : (
                            <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold border bg-blue-100 text-blue-700 border-blue-200">
                              {`${suitesAssociees.length} suite(s)`}
                            </span>
                          )
                        )}
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold border ${st.cls}`}>
                          {st.text}
                        </span>
                        <i className={`text-gray-400 ${isExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="px-4 pb-4 space-y-3 border-t border-gray-100 pt-3">
                        {/* Bouton Fiche de visite */}
                        {canFicheVisite && (
                          <div className="flex justify-end">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/medecin/fiche-visite?patientId=${patientId}&consultationId=${c.id}`);
                              }}
                              className="px-4 py-2 bg-clinic-blue/10 text-clinic-blue-dark border border-clinic-blue/30 rounded-xl text-sm font-semibold hover:bg-clinic-blue/15 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
                            >
                                                            Fiche de visite
                            </button>
                          </div>
                        )}

                        {c.symptomes_signes && (
                          <div>
                            <p className="text-xs font-semibold text-gray-600 mb-1">Symptômes & signes</p>
                            <p className="text-sm text-gray-800 whitespace-pre-wrap bg-gray-50 rounded-lg p-3">{c.symptomes_signes}</p>
                          </div>
                        )}
                        {c.examen && (
                          <div>
                            <p className="text-xs font-semibold text-gray-600 mb-1">Examen clinique</p>
                            <p className="text-sm text-gray-800 whitespace-pre-wrap bg-gray-50 rounded-lg p-3">{c.examen}</p>
                          </div>
                        )}
                        {c.diagnostic && (
                          <div>
                            <p className="text-xs font-semibold text-red-600 mb-1">Diagnostic</p>
                            <p className="text-sm text-red-800 bg-red-50 rounded-lg p-3">{c.diagnostic}</p>
                          </div>
                        )}
                        {c.suivi_vers && (
                          <p className="text-sm text-gray-700">
                            <span className="text-xs font-semibold text-gray-500">Suivi vers : </span>{c.suivi_vers}
                          </p>
                        )}
                        {c.mot_fin && (
                          <p className="text-sm text-gray-700 italic bg-gray-50 rounded-lg p-3">{c.mot_fin}</p>
                        )}
                        {soinsByConsultation[c.id] && soinsByConsultation[c.id].length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-clinic-blue mb-1 flex items-center gap-1.5">
                                                            Soins liés
                            </p>
                            <div className="space-y-2">
                              {soinsByConsultation[c.id].map((s) => (
                                <div key={`${s.source}-${s.id}`} className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-lg p-3">
                                  <div className="bg-blue-100 rounded px-2 py-0.5 text-[11px] font-semibold text-blue-700 whitespace-nowrap flex-shrink-0">
                                    {formatDate(s.date)}
                                  </div>
                                  <p className="text-sm text-blue-900 whitespace-pre-wrap flex-1">{s.description}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )
        )}

        {/* ===== SOINS ===== */}
        {sousOnglet === 'soins' && (
          soins.length === 0 ? (
            <EmptyState icon="ri-nurse-line" text="Aucun soin enregistré" />
          ) : (
            <div className="space-y-3">
              {soins.map((s) => {
                const estSuite = s.source === 'suite';
                return (
                  <div key={`${s.source}-${s.id}`} className={`border rounded-xl p-4 ${estSuite ? 'border-blue-200 bg-blue-50/30' : 'border-gray-200'}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 min-w-0">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-semibold text-gray-900">{formatDate(s.date)}</p>
                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold border ${estSuite ? 'bg-blue-100 text-blue-700 border-blue-200' : 'bg-clinic-blue/15 text-clinic-blue-dark border-clinic-blue/30'}`}>
                              {estSuite ? 'Suite de soins' : 'Consultation'}
                            </span>
                          </div>
                          {s.medecin && (
                            <p className="text-xs text-gray-500 mt-0.5">
                              Dr. {s.medecin.nom} {s.medecin.prenom}{s.medecin.specialite ? ` · ${s.medecin.specialite}` : ''}
                            </p>
                          )}
                        </div>
                      </div>
                      {s.statut && (
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold border flex-shrink-0 ${statutBadge(s.statut).cls}`}>
                          {statutBadge(s.statut).text}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-800 mt-3 bg-white rounded-lg p-3 border border-gray-100 whitespace-pre-wrap">{s.description}</p>
                  </div>
                );
              })}
            </div>
          )
        )}

        {/* ===== SUIVIS ===== */}
        {sousOnglet === 'suivis' && (
          suivis.length === 0 ? (
            <EmptyState icon="ri-send-plane-line" text="Aucun suivi" />
          ) : (
            <div className="space-y-3">
              {suivis.map((s) => {
                const st = statutBadge(s.statut);
                return (
                  <div key={s.id} className="border border-gray-200 rounded-xl p-4">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex items-center gap-2 min-w-0">
                                                <p className="text-sm font-semibold text-gray-900 truncate">{s.objet || 'Suivi patient'}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-xs text-gray-400">{formatDate(s.date_creation)}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold border ${st.cls}`}>{st.text}</span>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 space-y-1 mb-2">
                      {s.medecin_expediteur && (
                        <p>De : Dr. {s.medecin_expediteur.nom} {s.medecin_expediteur.prenom} ({s.medecin_expediteur.specialite || '—'})</p>
                      )}
                      {s.medecin_destinataire && (
                        <p>À : Dr. {s.medecin_destinataire.nom} {s.medecin_destinataire.prenom} ({s.medecin_destinataire.specialite || '—'})</p>
                      )}
                    </div>
                    {s.message && <p className="text-sm text-gray-800 bg-gray-50 rounded-lg p-3 mb-2">{s.message}</p>}
                    {s.diagnostique && (
                      <p className="text-sm text-gray-700"><span className="text-xs font-semibold text-gray-500">Diagnostic : </span>{s.diagnostique}</p>
                    )}
                    {s.traitement && (
                      <p className="text-sm text-gray-700"><span className="text-xs font-semibold text-gray-500">Traitement : </span>{s.traitement}</p>
                    )}
                  </div>
                );
              })}
            </div>
          )
        )}

        {/* ===== PRESCRIPTIONS ===== */}
        {sousOnglet === 'prescriptions' && (
          prescriptions.length === 0 ? (
            <EmptyState icon="ri-file-text-line" text="Aucune prescription" />
          ) : (
            <div className="space-y-3">
              {prescriptions.map((p) => {
                const isSortie = p.type_ordonnance === 'sortie';
                return (
                  <div key={p.id} className={`border rounded-xl p-4 ${isSortie ? 'border-amber-200 bg-amber-50/30' : 'border-gray-200'}`}>
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex items-center gap-2">
                                                <p className="text-sm font-semibold text-gray-900">
                          {isSortie ? 'Ordonnance de sortie' : 'Ordonnance'}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-xs text-gray-400">{formatDate(p.date_prescription)}</p>
                        {p.medecins && (
                          <p className="text-xs text-gray-500">Dr. {p.medecins.nom} {p.medecins.prenom}</p>
                        )}
                      </div>
                    </div>
                    {p.diagnostic && (
                      <p className="text-sm text-gray-700 mb-2"><span className="text-xs font-semibold text-gray-500">Diagnostic : </span>{p.diagnostic}</p>
                    )}
                    <div className="bg-white rounded-lg p-3 border border-gray-100 mb-2">
                      {renderMedicaments(p.medicaments) || <p className="text-sm text-gray-400 italic">Aucun médicament listé</p>}
                    </div>
                    {p.duree_traitement && (
                      <p className="text-sm text-gray-700"><span className="text-xs font-semibold text-gray-500">Durée : </span>{p.duree_traitement}</p>
                    )}
                    {p.instructions && (
                      <p className="text-sm text-gray-700 mt-1"><span className="text-xs font-semibold text-gray-500">Instructions : </span>{p.instructions}</p>
                    )}
                  </div>
                );
              })}
            </div>
          )
        )}

        {/* ===== ANALYSES ===== */}
        {sousOnglet === 'analyses' && (
          analyses.length === 0 ? (
            <EmptyState icon="ri-microscope-line" text="Aucun résultat d'analyse" />
          ) : (
            <div className="space-y-3">
              {analyses.map((a) => {
                const st = statutBadge(a.statut);
                return (
                  <div key={a.id} className="border border-gray-200 rounded-xl p-4 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900">Dossier d'analyses</p>
                        <p className="text-xs text-gray-500">{formatDate(a.date_analyse)}</p>
                        {a.conclusion && (
                          <p className="text-xs text-gray-500 truncate max-w-[300px]">{a.conclusion}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold border ${st.cls}`}>{st.text}</span>
                      <button
                        onClick={() => navigate(`/laborantin/rapport-analyses/${a.id}`)}
                        className="px-3 py-1.5 bg-clinic-blue text-white rounded-full text-xs font-semibold hover:bg-clinic-blue-dark transition-colors whitespace-nowrap cursor-pointer"
                      >
                        Voir le rapport
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        )}
      </div>
    </div>
  );
}

function EmptyState({ icon, text }: { icon: string; text: string }) {
  return (
    <div className="py-12 text-center">
      <i className={`${icon} text-4xl text-gray-300`}></i>
      <p className="mt-3 text-sm text-gray-500">{text}</p>
    </div>
  );
}